import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Award, Phone, Shield, FileText, Settings, Users, Mail } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import ContentPageLayout from '../components/ContentPageLayout';

const Gewaehrleistung = () => {
  const { t } = useTranslation();

  // Gewährleistungs-Kategorien
  const gewaehrleistungCategories = [
    {
      id: 'unterschied',
      title: 'Garantie vs. Gewährleistung – Der Unterschied',
      icon: '⚖️',
      questions: [
        {
          id: '1',
          question: 'Was ist der Unterschied zwischen Garantie und Gewährleistung?',
          answer: 'Die Gewährleistung ist ein gesetzlicher Anspruch, der Ihnen als Käufer zusteht. Sie regelt, dass der Verkäufer für Mängel haftet, die bereits bei Übergabe der Ware bestanden. Die Garantie hingegen ist eine freiwillige Zusatzleistung des Herstellers oder Händlers, die über die gesetzlichen Rechte hinausgeht. Bei Jannesmann erhalten Sie beides: die gesetzliche Gewährleistung und zusätzlich unsere 12-monatige Qualitätsgarantie.'
        },
        {
          id: '2',
          question: 'Welche Rechte habe ich bei der Gewährleistung?',
          answer: 'Bei einem Mangel haben Sie zunächst Anspruch auf Nacherfüllung – also Reparatur oder Ersatzlieferung. Erst wenn die Nacherfüllung fehlschlägt (in der Regel nach zwei Versuchen), können Sie vom Vertrag zurücktreten oder den Kaufpreis mindern. Wir setzen alles daran, Mängel schnell und unkompliziert zu beheben.'
        },
        {
          id: '3',
          question: 'Wann gilt die Garantie, wann die Gewährleistung?',
          answer: 'Die Gewährleistung greift bei Mängeln, die bereits bei Lieferung vorhanden waren (z.B. Materialfehler, Fertigungsfehler). Die Garantie kann auch bei später auftretenden Defekten greifen, sofern diese unter die Garantiebedingungen fallen. Verschleißteile und Schäden durch unsachgemäße Nutzung sind in der Regel von beiden ausgeschlossen.'
        }
      ]
    },
    {
      id: 'fristen',
      title: 'Gewährleistungsfristen',
      icon: '📅',
      questions: [
        {
          id: '4',
          question: 'Wie lange gilt die Gewährleistung für Verbraucher?',
          answer: 'Für Verbraucher (Privatkunden) gilt die gesetzliche Gewährleistungsfrist von 24 Monaten ab Übergabe der Ware. In den ersten 12 Monaten wird vermutet, dass ein auftretender Mangel bereits bei Übergabe vorhanden war (Beweislastumkehr). Nach 12 Monaten müssen Sie als Käufer nachweisen, dass der Mangel von Anfang an bestand.'
        },
        {
          id: '5',
          question: 'Welche Gewährleistungsfrist gilt für gewerbliche Kunden?',
          answer: 'Für Unternehmer und gewerbliche Kunden im Sinne des § 14 BGB beträgt die Gewährleistungsfrist 12 Monate ab Übergabe. Diese verkürzte Frist ist gesetzlich zulässig und branchenüblich im Bereich Baumaschinen und Nutzfahrzeuge.'
        },
        {
          id: '6',
          question: 'Wann beginnt die Gewährleistungsfrist?',
          answer: 'Die Gewährleistungsfrist beginnt mit der Übergabe der Ware an Sie oder an den von Ihnen beauftragten Spediteur. Das Datum auf Ihrem Lieferschein bzw. Ihrer Rechnung ist maßgeblich. Bei Selbstabholung beginnt die Frist mit der Abholung am Standort.'
        }
      ]
    },
    {
      id: 'ablauf',
      title: 'Ablauf im Gewährleistungsfall',
      icon: '🔄',
      questions: [
        {
          id: '7',
          question: 'Wie melde ich einen Mangel?',
          answer: 'Kontaktieren Sie uns umgehend per E-Mail an info@jannesmann.de oder telefonisch unter 02041 9877499. Beschreiben Sie den Mangel möglichst genau und fügen Sie Fotos oder Videos bei. Halten Sie Ihre Rechnungsnummer und die Seriennummer der Maschine bereit. Je detaillierter Ihre Angaben, desto schneller können wir helfen.'
        },
        {
          id: '8',
          question: 'Wie läuft die Prüfung ab?',
          answer: 'Nach Eingang Ihrer Meldung prüfen wir den Fall anhand Ihrer Angaben und Dokumentation. In vielen Fällen können wir bereits aus der Ferne eine erste Einschätzung geben. Bei Bedarf fordern wir weitere Informationen an oder vereinbaren eine technische Begutachtung. Wir informieren Sie zeitnah über das Ergebnis der Prüfung.'
        },
        {
          id: '9',
          question: 'Wie erfolgt die Reparatur oder der Austausch?',
          answer: 'Bei berechtigten Gewährleistungsansprüchen entscheiden wir, ob eine Reparatur oder ein Austausch des betroffenen Bauteils erfolgt. Kleinere Ersatzteile senden wir Ihnen direkt zu. Bei größeren Reparaturen organisieren wir den Rücktransport der Maschine oder des Bauteils. Reparaturen führen wir selbst durch oder beauftragen eine Partnerwerkstatt in Ihrer Nähe.'
        },
        {
          id: '10',
          question: 'Muss ich die Maschine einsenden?',
          answer: 'Das hängt vom Einzelfall ab. Bei kleineren Defekten senden wir Ihnen oft Ersatzteile mit Einbauanleitung zu. Bei größeren Schäden kann eine Einsendung der Maschine oder des betroffenen Bauteils notwendig sein. In diesem Fall organisieren und tragen wir die Transportkosten bei berechtigten Gewährleistungsansprüchen.'
        }
      ]
    },
    {
      id: 'faelle',
      title: 'Typische Gewährleistungsfälle',
      icon: '🔧',
      questions: [
        {
          id: '11',
          question: 'Was passiert bei einem defekten Bauteil?',
          answer: 'Wenn ein Bauteil aufgrund eines Material- oder Fertigungsfehlers defekt ist, ersetzen wir es kostenfrei. Wir senden Ihnen das Ersatzteil zu oder tauschen es vor Ort aus. In der Regel bearbeiten wir solche Fälle innerhalb von 5-10 Werktagen.'
        },
        {
          id: '12',
          question: 'Was gilt bei Funktionsstörungen?',
          answer: 'Funktionsstörungen, die auf Fertigungsfehler zurückzuführen sind, fallen unter die Gewährleistung. Unser technischer Support hilft Ihnen zunächst telefonisch bei der Fehlerdiagnose. Lässt sich das Problem nicht aus der Ferne lösen, organisieren wir eine Reparatur.'
        },
        {
          id: '13',
          question: 'Wie gehe ich bei Transportschäden vor?',
          answer: 'Transportschäden müssen Sie sofort bei Anlieferung dokumentieren und bei der Spedition reklamieren. Vermerken Sie den Schaden auf dem Lieferschein und fotografieren Sie ihn umgehend. Melden Sie den Schaden innerhalb von 24 Stunden an uns. Nur so können wir den Anspruch gegenüber der Spedition geltend machen.'
        },
        {
          id: '14',
          question: 'Sind Verschleißteile von der Gewährleistung abgedeckt?',
          answer: 'Nein, Verschleißteile wie Filter, Dichtungen, Riemen, Bremsbeläge, Reifen, Ketten und Batterien unterliegen normalem Verschleiß und sind von der Gewährleistung ausgenommen. Defekte an Verschleißteilen, die auf einen Fertigungsfehler zurückzuführen sind, werden jedoch natürlich anerkannt.'
        }
      ]
    },
    {
      id: 'kosten',
      title: 'Kostenregelung',
      icon: '💶',
      questions: [
        {
          id: '15',
          question: 'Welche Kosten übernimmt Jannesmann?',
          answer: 'Bei berechtigten Gewährleistungsansprüchen übernehmen wir die Kosten für Ersatzteile, Reparatur und Transport. Sie zahlen nichts. Falls sich herausstellt, dass kein Gewährleistungsfall vorliegt (z.B. bei Verschleiß oder Fehlbedienung), informieren wir Sie vorab über anfallende Kosten.'
        },
        {
          id: '16',
          question: 'Was kostet der Versand im Gewährleistungsfall?',
          answer: 'Bei anerkannten Gewährleistungsfällen übernehmen wir die Versandkosten für Ersatzteile sowie ggf. den Rücktransport der Maschine. Bei unberechtigten Reklamationen oder Schäden außerhalb der Gewährleistung tragen Sie die Transportkosten.'
        },
        {
          id: '17',
          question: 'Muss ich in Vorleistung gehen?',
          answer: 'Nein, bei eindeutigen Gewährleistungsfällen müssen Sie nicht in Vorleistung gehen. Wir senden Ersatzteile auf unsere Kosten. Bei unklaren Fällen kann es vorkommen, dass wir zunächst um Einsendung des Bauteils bitten, um den Sachverhalt zu prüfen.'
        }
      ]
    },
    {
      id: 'dauer',
      title: 'Bearbeitungsdauer & Fristen',
      icon: '⏱️',
      questions: [
        {
          id: '18',
          question: 'Wie lange dauert die Bearbeitung eines Gewährleistungsfalls?',
          answer: 'Wir bearbeiten Gewährleistungsanfragen so schnell wie möglich. Eine erste Rückmeldung erhalten Sie in der Regel innerhalb von 1-2 Werktagen. Ersatzteile versenden wir meist innerhalb von 3-5 Werktagen. Bei aufwändigeren Reparaturen informieren wir Sie über die voraussichtliche Dauer.'
        },
        {
          id: '19',
          question: 'Gibt es eine Frist für die Mängelmeldung?',
          answer: 'Sie sollten Mängel unverzüglich nach Entdeckung melden. Je früher Sie uns informieren, desto besser können wir helfen. Bei offensichtlichen Mängeln (z.B. Transportschäden) ist eine sofortige Meldung erforderlich. Versteckte Mängel sollten Sie melden, sobald Sie diese bemerken.'
        },
        {
          id: '20',
          question: 'Verlängert sich die Gewährleistung durch eine Reparatur?',
          answer: 'Bei der gesetzlichen Gewährleistung beginnt die Frist für das reparierte oder ausgetauschte Teil nicht neu. Es gilt weiterhin die ursprüngliche Gewährleistungsfrist ab Kaufdatum. Anders verhält es sich bei unserer freiwilligen Garantie – hier gelten die jeweiligen Garantiebedingungen.'
        }
      ]
    },
    {
      id: 'kontakt',
      title: 'Ansprechpartner & Kontakt',
      icon: '📞',
      questions: [
        {
          id: '21',
          question: 'Wer ist mein Ansprechpartner für Gewährleistungsfälle?',
          answer: 'Unser Kundenservice-Team ist Ihr erster Ansprechpartner. Sie erreichen uns telefonisch unter 02041 9877499 (Mo-Fr 08:00-18:00 Uhr) oder per E-Mail an info@jannesmann.de. Bei technischen Fragen leiten wir Sie an unsere Techniker weiter.'
        },
        {
          id: '22',
          question: 'Welche Informationen benötigen Sie für die Bearbeitung?',
          answer: 'Für eine schnelle Bearbeitung benötigen wir: 1) Ihre Rechnungsnummer oder Bestellnummer, 2) Die Seriennummer der Maschine (auf dem Typenschild), 3) Eine genaue Beschreibung des Mangels, 4) Fotos oder Videos des Defekts, 5) Ihre Kontaktdaten für Rückfragen.'
        },
        {
          id: '23',
          question: 'Kann ich den Gewährleistungsantrag online stellen?',
          answer: 'Ja, Sie können Ihren Gewährleistungsantrag per E-Mail an info@jannesmann.de senden. Beschreiben Sie den Sachverhalt und fügen Sie alle relevanten Unterlagen bei. Alternativ können Sie uns auch über WhatsApp kontaktieren oder das Kontaktformular auf unserer Website nutzen.'
        }
      ]
    },
    {
      id: 'download',
      title: 'Formulare & Downloads',
      icon: '📄',
      questions: [
        {
          id: '24',
          question: 'Gibt es ein Formular für den Gewährleistungsantrag?',
          answer: 'Sie können Ihren Gewährleistungsantrag formlos per E-Mail stellen. Achten Sie darauf, alle relevanten Informationen (Rechnungsnummer, Seriennummer, Mangelbeschreibung, Fotos) beizufügen. Ein standardisiertes Formular ist nicht erforderlich, erleichtert aber die Bearbeitung.'
        },
        {
          id: '25',
          question: 'Wo finde ich die Garantiebedingungen?',
          answer: 'Unsere vollständigen Garantiebedingungen finden Sie auf unserer Website unter dem Menüpunkt "Garantiebedingungen". Dort sind alle Details zu Umfang, Ausschlüssen und Abwicklung der 12-monatigen Jannesmann Qualitätsgarantie aufgeführt.'
        }
      ]
    }
  ];

  return (
    <ContentPageLayout>
      {/* Hauptbereich */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]" data-testid="gewaehrleistung-page">
          
          {/* Überschrift */}
          <h1 className="text-lg sm:text-xl font-semibold text-white bg-gray-900 px-6 py-3 rounded-t-lg flex items-center" data-testid="gewaehrleistung-title">
            <Shield className="mr-3" size={24} />
            Gewährleistung & Serviceabwicklung
          </h1>

          {/* Intro-Text */}
          <div className="bg-gray-50 border border-gray-200 border-t-0 p-6 md:p-8 rounded-b-xl">
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
              Bei Jannesmann Baumaschinen steht Qualität an erster Stelle. Sollte dennoch einmal ein Mangel auftreten, 
              sind wir für Sie da. Hier finden Sie alle wichtigen Informationen zur gesetzlichen Gewährleistung und 
              zur Abwicklung im Servicefall.
            </p>
            
            {/* Quick-Info Karten */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-start">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <Users className="text-green-600" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Verbraucher</h3>
                  <p className="text-sm text-gray-600">24 Monate Gewährleistung</p>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-start">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <Settings className="text-blue-600" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Gewerblich</h3>
                  <p className="text-sm text-gray-600">12 Monate Gewährleistung</p>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-start">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <Award className="text-red-600" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">+ Garantie</h3>
                  <p className="text-sm text-gray-600">12 Monate Qualitätsgarantie</p>
                </div>
              </div>
            </div>

            {/* Kategorien mit Accordion */}
            <div className="space-y-6">
              {gewaehrleistungCategories.map((category) => (
                <div key={category.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                  {/* Kategorie-Header */}
                  <div className="bg-red-600 text-white px-4 py-3">
                    <h2 className="text-base sm:text-lg font-semibold flex items-center">
                      <span className="mr-3 text-xl">{category.icon}</span>
                      {category.title}
                    </h2>
                  </div>
                  
                  {/* Fragen Accordion */}
                  <Accordion type="single" collapsible className="px-4">
                    {category.questions.map((item) => (
                      <AccordionItem 
                        key={item.id} 
                        value={`${category.id}-${item.id}`}
                        className="border-b border-gray-100 last:border-0"
                      >
                        <AccordionTrigger className="text-left py-4 text-sm sm:text-base font-medium text-gray-900 hover:text-red-600 hover:no-underline">
                          {item.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-sm sm:text-base text-gray-600 leading-relaxed pb-4">
                          {item.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}
            </div>

            {/* Kontakt-Hinweis */}
            <div className="mt-8 bg-gray-100 rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <Phone className="mr-2 text-red-600" size={20} />
                Gewährleistungsfall melden
              </h3>
              <p className="text-gray-700 mb-4">
                Sie haben einen Mangel entdeckt? Kontaktieren Sie uns – wir helfen Ihnen schnell und unkompliziert:
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                <a 
                  href="tel:+4920419877499" 
                  className="inline-flex items-center justify-center bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  <Phone className="mr-2" size={18} />
                  02041 9877499
                </a>
                <a 
                  href="mailto:info@jannesmann.de?subject=Gewährleistungsanfrage" 
                  className="inline-flex items-center justify-center bg-gray-800 hover:bg-gray-900 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  <Mail className="mr-2" size={18} />
                  info@jannesmann.de
                </a>
                <Link 
                  to="/garantiebedingungen"
                  className="inline-flex items-center justify-center bg-white border-2 border-red-600 text-red-600 hover:bg-red-50 px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  <FileText className="mr-2" size={18} />
                  Garantiebedingungen
                </Link>
              </div>
              <p className="text-sm text-gray-500">
                <strong>Tipp:</strong> Halten Sie für eine schnelle Bearbeitung Ihre Rechnungsnummer, die Seriennummer 
                der Maschine sowie Fotos des Mangels bereit.
              </p>
            </div>
          </div>
        </div>
      </section>
    </ContentPageLayout>
  );
};

export default Gewaehrleistung;
